import { useEffect, useState } from "react";
import { api, auth as authApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { TechDevicesList } from "@/components/TechDevicesList";
import { ArmadaRecoveryButton } from "@/components/ArmadaRecovery";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Plus, Sparkles, Trash2, Calendar as CalIcon, CheckCircle2, MapPin, RefreshCw, Wand2, KeyRound, Copy, Navigation, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";

const SKILLS = ["AC_REPAIR","AC_INSTALL","CLEANING","CHILLER","HVAC"];
const COLORS = ["#E65100","#2962FF","#059669","#B45309","#7C3AED","#DC2626","#0891B2","#EA580C","#4338CA"];

export default function Technicians() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank());
  const [gCals, setGCals] = useState([]);
  const [gStatus, setGStatus] = useState(null);
  const [zones, setZones] = useState([]);
  const [zoneSettings, setZoneSettings] = useState(null);
  const [busy, setBusy] = useState(false);
  const [traccarDiag, setTraccarDiag] = useState(null);  // v84.23 diagnostic result
  const [traccarBusy, setTraccarBusy] = useState(false);
  // v85.34 — Resume WO harian ke teknisi (manual trigger)
  const [resumeBusyId, setResumeBusyId] = useState(null);   // null = idle, "ALL" = kirim semua, else = tech_id
  const [resumeAllBusy, setResumeAllBusy] = useState(false);
  const [resumePreview, setResumePreview] = useState(null); // {name, text, total, special_count}

  async function sendResume(tech, { dryRun = false } = {}) {
    setResumeBusyId(tech.id);
    try {
      const r = await api.sendTechDailyResume({ tech_id: tech.id, dry_run: dryRun });
      const res = r?.result || {};
      if (res.skipped === "no_phone") {
        toast.error(`${tech.name}: nomor WA belum diisi di profil`);
      } else if (res.skipped === "no_wo_today") {
        toast.info(`${tech.name}: tidak ada WO hari ini`);
      } else if (dryRun) {
        setResumePreview({ name: res.name, text: res.preview,
                           total: res.total, special_count: res.special_count });
      } else if (res.ok) {
        toast.success(`✓ Resume terkirim ke ${res.name} (${res.total} WO, ${res.special_count} khusus)`);
      } else {
        toast.error(`Gagal kirim: ${JSON.stringify(res.wa_result || res)}`);
      }
    } catch (e) {
      toast.error("Gagal: " + (e?.response?.data?.detail || e.message));
    } finally {
      setResumeBusyId(null);
    }
  }

  async function sendResumeAll() {
    if (!window.confirm("Kirim resume harian ke SEMUA teknisi via WhatsApp sekarang?")) return;
    setResumeAllBusy(true);
    try {
      const r = await api.sendTechDailyResume({});
      toast.success(`✓ Resume dikirim ke ${r.sent} teknisi (skip: ${r.skipped})`);
    } catch (e) {
      toast.error("Gagal: " + (e?.response?.data?.detail || e.message));
    } finally {
      setResumeAllBusy(false);
    }
  }
  function blank() { return { name:"", phone:"", skills:["HVAC","AC_REPAIR"], base_lat:-6.2088, base_lng:106.8456, base_address:"Jakarta", color:"#E65100", active:true, traccar_id:"" }; }

  async function regenToken(a) {
    if (!window.confirm(`Buat token GPS baru untuk ${a.tech_name}? Token lama langsung tidak berlaku, URL di Traccar Client HP harus diganti.`)) return;
    try {
      const r = await api.regenerateGpsToken(a.tech_id);
      await navigator.clipboard?.writeText(r.traccar_server_url).catch(() => {});
      toast.success(`Token baru ${a.tech_name}: ${r.gps_token} — URL disalin`, { duration: 10000 });
      setTraccarDiag(d => d && ({ ...d, armada: d.armada.map(x => x.tech_id === a.tech_id ? { ...x, gps_token: r.gps_token, traccar_server_url: r.traccar_server_url } : x) }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal membuat token baru");
    }
  }

  // v84.23 — Load status Traccar semua armada (yg punya traccar_id) + orphan pings
  async function loadTraccarDiag() {
    setTraccarBusy(true);
    try {
      const r = await api.traccarDiagnose();
      setTraccarDiag(r);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal ambil diagnostik");
    } finally {
      setTraccarBusy(false);
    }
  }

  async function load() {
    setItems(await api.listTechs());
    const gs = await api.googleStatus();
    setGStatus(gs);
    if (gs.connected) {
      try { setGCals(await api.googleCalendars()); } catch { setGCals([]); }
    }
    try {
      const z = await api.listZones();
      setZones(z.zones || []);
      setZoneSettings(z.settings || null);
    } catch { setZones([]); }
  }
  useEffect(() => { load(); }, []);

  async function linkCal(techId, calId) {
    await api.linkCal(techId, calId);
    toast.success("Kalender di-link");
    load();
  }

  async function setTechZone(tech, zone_key) {
    // Kirim payload TechnicianIn lengkap (endpoint PUT expects full body)
    const payload = {
      name: tech.name, phone: tech.phone || "", skills: tech.skills || [],
      base_lat: tech.base_lat, base_lng: tech.base_lng,
      base_address: tech.base_address || "", color: tech.color,
      active: tech.active !== false,
      google_calendar_id: tech.google_calendar_id || null,
      zone_key: zone_key || null,
    };
    await api.updateTech(tech.id, payload);
    toast.success(zone_key ? `Zona di-set: ${zone_key}` : "Zona dikosongkan");
    load();
  }

  async function refreshZonesNow() {
    if (!zoneSettings?.map_id) {
      toast.error("Map ID belum diset. Upload KML dulu di Pengaturan.");
      return;
    }
    setBusy(true);
    try {
      const r = await api.refreshZones();
      toast.success(`Ter-refresh: ${r.count} zona di-load dari Google My Maps`);
      load();
    } catch (e) {
      toast.error(`Gagal refresh zona: ${e?.response?.data?.detail || e.message}`);
    } finally { setBusy(false); }
  }

  async function autoMapZones() {
    setBusy(true);
    try {
      const r = await api.autoMapZones();
      if (r.mapped > 0) toast.success(`${r.mapped} teknisi otomatis di-mapping ke zona sesuai nama`);
      else if (r.reason) toast.error(r.reason);
      else toast.info(`Semua teknisi sudah punya zona (skipped ${r.skipped_already_set})`);
      load();
    } catch (e) {
      toast.error(`Auto-map gagal: ${e?.response?.data?.detail || e.message}`);
    } finally { setBusy(false); }
  }

  // ── Password Armada management ─────────────────────────────
  const [pwDialog, setPwDialog] = useState(null);  // {tech, username, password}
  function openPwDialog(tech) {
    setPwDialog({
      tech,
      username: tech.username || tech.name || "",
      password: "",
    });
  }
  async function savePw() {
    if (!pwDialog.password || pwDialog.password.length < 6) {
      toast.error("Password minimal 6 karakter"); return;
    }
    try {
      const r = await authApi.setTechPassword(pwDialog.tech.id, {
        username: pwDialog.username,
        password: pwDialog.password,
      });
      toast.success(`Password armada diset. Username: ${r.username}`);
      setPwDialog(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal set password");
    }
  }
  function copyCred() {
    const txt = `ID Armada: ${pwDialog.username}\nPassword: ${pwDialog.password}\nLogin: ${window.location.origin}/tech-login`;
    navigator.clipboard.writeText(txt);
    toast.success("Kredensial disalin ke clipboard");
  }

  async function save() {
    if (!form.name) { toast.error("Nama wajib"); return; }
    try {
      await api.createTech(form);
      toast.success("Tim Armada ditambahkan"); setOpen(false); setForm(blank()); load();
    } catch { toast.error("Gagal simpan"); }
  }
  async function del(id) {
    await api.deleteTech(id);
    toast.success("Tim Armada dihapus");
    load();
  }

  // v84.22 — Update Traccar Device ID untuk 1 armada
  async function saveTraccarId(t, newId) {
    try {
      const payload = {
        name: t.name, phone: t.phone || "", skills: t.skills,
        base_lat: t.base_lat, base_lng: t.base_lng,
        base_address: t.base_address, color: t.color,
        active: t.active,
        google_calendar_id: t.google_calendar_id || null,
        zone_key: t.zone_key || null,
        traccar_id: (newId || "").trim() || null,
      };
      await api.updateTech(t.id, payload);
      toast.success(`Traccar Device ID diset: ${newId || "(kosong)"}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal simpan Traccar ID");
    }
  }
  async function seed() {
    const r = await api.seedTechs();
    if (r.skipped) toast.info(`Sudah ada ${r.existing} teknisi`);
    else toast.success(`${r.seeded} teknisi contoh berhasil di-seed`);
    load();
  }

  function toggleSkill(s) {
    setForm(f => ({...f, skills: f.skills.includes(s) ? f.skills.filter(x=>x!==s) : [...f.skills, s] }));
  }

  return (
    <div className="space-y-4" data-testid="technicians-page">
      <div className="flex items-center justify-between">
        <div className="text-[12px] text-zinc-500">
          Total: <b className="font-mono text-zinc-800">{items.length}</b> tim armada
          {zones.length > 0 && (
            <span className="ml-3">· <b className="font-mono text-zinc-800">{zones.length}</b> zona aktif</span>
          )}
        </div>
        <div className="flex gap-2 flex-wrap">
          <ArmadaRecoveryButton onRestored={load} />
          <Button variant="outline" onClick={refreshZonesNow} disabled={busy || !zoneSettings?.map_id}
            title={zoneSettings?.map_id ? "Refresh zona dari Google My Maps" : "Upload KML dulu di Pengaturan"}
            className="rounded-sm gap-1.5" data-testid="refresh-zones-btn">
            <RefreshCw className={`w-4 h-4 ${busy?'animate-spin':''}`} />Refresh Zona
          </Button>
          <Button variant="outline" onClick={autoMapZones} disabled={busy || zones.length===0}
            title="Otomatis mapping teknisi ke zona berdasarkan pola nama"
            className="rounded-sm gap-1.5" data-testid="auto-map-zones-btn">
            <Wand2 className="w-4 h-4" />Auto-map Zona
          </Button>
          <Button variant="outline" onClick={seed} className="rounded-sm gap-1.5" data-testid="seed-btn"><Sparkles className="w-4 h-4" />Seed 9 Contoh</Button>
          <Button variant="outline" onClick={loadTraccarDiag} disabled={traccarBusy}
            className="rounded-sm gap-1.5" data-testid="traccar-diag-btn">
            <Navigation className={`w-4 h-4 ${traccarBusy?'animate-pulse':''}`} />Diagnostik Traccar
          </Button>
          {/* v85.34 — Resume WO harian ke SEMUA teknisi via WA */}
          <Button variant="outline" onClick={sendResumeAll} disabled={resumeAllBusy}
            className="rounded-sm gap-1.5 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
            data-testid="send-resume-all-btn"
            title="Kirim resume WO hari ini ke SEMUA teknisi via WhatsApp">
            {resumeAllBusy
              ? <><Loader2 className="w-4 h-4 animate-spin" /> Mengirim…</>
              : <><Send className="w-4 h-4" /> Kirim Resume ke Semua</>}
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-tech-btn" className="bg-[#E65100] hover:bg-[#F57C00] rounded-sm gap-1.5"><Plus className="w-4 h-4" />Tambah Tim Armada</Button>
            </DialogTrigger>
            <DialogContent className="rounded-sm">
              <DialogHeader><DialogTitle>Tim Armada Baru</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <Input placeholder="Nama" value={form.name} onChange={e=>setForm({...form, name:e.target.value})} className="rounded-sm" data-testid="tech-name-input" />
                <Input placeholder="Nomor HP" value={form.phone} onChange={e=>setForm({...form, phone:e.target.value})} className="rounded-sm" data-testid="tech-phone-input" />
                <Input placeholder="Alamat/Area base" value={form.base_address} onChange={e=>setForm({...form, base_address:e.target.value})} className="rounded-sm" />
                <div className="grid grid-cols-2 gap-2">
                  <Input placeholder="Latitude" type="number" step="0.0001" value={form.base_lat} onChange={e=>setForm({...form, base_lat:parseFloat(e.target.value)})} className="rounded-sm" />
                  <Input placeholder="Longitude" type="number" step="0.0001" value={form.base_lng} onChange={e=>setForm({...form, base_lng:parseFloat(e.target.value)})} className="rounded-sm" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">Keahlian</div>
                  <div className="flex flex-wrap gap-1.5">
                    {SKILLS.map(s => (
                      <button key={s} type="button" onClick={()=>toggleSkill(s)}
                        className={`px-2.5 py-1 rounded-sm text-[11px] border transition-colors ${form.skills.includes(s) ? "bg-[#E65100] text-white border-[#E65100]" : "bg-white border-border hover:border-[#E65100]"}`}>
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">Warna</div>
                  <div className="flex gap-1.5">
                    {COLORS.map(c => (
                      <button key={c} type="button" onClick={()=>setForm({...form, color:c})}
                        className={`w-7 h-7 rounded-full border-2 ${form.color===c?'border-black':'border-transparent'}`}
                        style={{background:c}} />
                    ))}
                  </div>
                </div>
                <Button onClick={save} className="w-full bg-[#E65100] hover:bg-[#F57C00] rounded-sm" data-testid="tech-save-btn">Simpan</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* v84.23 — Traccar diagnostic panel */}
      {traccarDiag && (
        <div className="p-3 rounded-sm border border-emerald-200 bg-emerald-50/50" data-testid="traccar-diag-panel">
          <div className="flex items-center justify-between mb-2">
            <div className="font-semibold text-[13px] text-emerald-900 flex items-center gap-1.5">
              <Navigation className="w-3.5 h-3.5" />
              Status Traccar per Armada
            </div>
            <button onClick={() => setTraccarDiag(null)}
              className="text-[10px] text-zinc-500 hover:text-zinc-700"
              data-testid="traccar-diag-close">Tutup ✕</button>
          </div>
          {traccarDiag.armada.length === 0 ? (
            <div className="text-[11px] text-zinc-600 italic">
              Belum ada armada yg di-set Traccar ID-nya.
            </div>
          ) : (
            <div className="space-y-1.5">
              {traccarDiag.armada.map(a => {
                const loc = a.location;
                const fresh = loc?.is_fresh ?? loc?.is_fresh_under_30min;
                return (
                  <div key={a.tech_id}
                    className="flex items-center gap-2 text-[11px] bg-white rounded-sm px-2 py-1.5 border border-zinc-100"
                    data-testid={`traccar-diag-row-${a.tech_id}`}>
                    <span className={`inline-block w-2 h-2 rounded-full ${fresh?'bg-emerald-500 animate-pulse':loc?'bg-amber-500':'bg-zinc-300'}`} />
                    <span className="font-semibold text-zinc-800 w-32 truncate">{a.tech_name}</span>
                    <span className="text-zinc-500 font-mono">ID:{a.traccar_id}</span>
                    {loc ? (
                      <>
                        <span className={fresh ? 'text-emerald-700 font-medium' : 'text-amber-700'}>
                          {fresh ? '🟢 ONLINE' : '🟡 STALE'} ({loc.age_minutes} min)
                        </span>
                        <span className="text-zinc-400 font-mono truncate">
                          {loc.lat?.toFixed?.(4)},{loc.lng?.toFixed?.(4)}
                        </span>
                      </>
                    ) : (
                      <span className="text-zinc-400 italic">Belum ada lokasi dari Traccar</span>
                    )}
                    <span className="ml-auto text-zinc-400">{a.pings_last_1h} ping/1jam</span>
                    <button className="text-zinc-500 hover:text-zinc-800 inline-flex items-center gap-1"
                      title={a.traccar_server_url}
                      onClick={() => { navigator.clipboard.writeText(a.traccar_server_url); toast.success(`URL Traccar ${a.tech_name} disalin`); }}
                      data-testid={`traccar-copy-url-${a.tech_id}`}>
                      <Copy className="w-3 h-3" />URL
                    </button>
                    <button className="text-orange-600 hover:text-orange-800 inline-flex items-center gap-1 font-medium"
                      onClick={() => regenToken(a)}
                      data-testid={`traccar-regen-token-${a.tech_id}`}>
                      <KeyRound className="w-3 h-3" />Token baru
                    </button>
                  </div>
                );
              })}
            </div>
          )}
          {traccarDiag.orphan_pings_last_1h?.length > 0 && (
            <div className="mt-3 pt-2 border-t border-amber-200">
              <div className="text-[11px] font-semibold text-amber-800 mb-1">
                ⚠️ Ping Traccar tidak dikenal (traccar_id belum di-mapping):
              </div>
              <div className="space-y-0.5">
                {traccarDiag.orphan_pings_last_1h.slice(0, 5).map((p, i) => (
                  <div key={i} className="text-[10px] font-mono text-amber-900 pl-3">
                    device_id=<b>{p.device_id || '(kosong)'}</b> · lat={p.lat},lon={p.lon}
                    · {new Date(p.created_at).toLocaleTimeString('id-ID')}
                  </div>
                ))}
              </div>
              <div className="text-[10px] text-amber-700 mt-1 pl-3 leading-snug">
                💡 Tip: Copy device_id di atas → paste ke kolom "Device ID" armada yg sesuai.
              </div>
            </div>
          )}
          <div className="text-[10px] text-zinc-500 mt-2 pt-2 border-t border-zinc-100">
            Server time: {traccarDiag.server_time_utc?.slice(0,19)} UTC · Threshold ONLINE: {traccarDiag.stale_threshold_minutes}min
          </div>
          {/* v84.26 — Traccar Android config guidance */}
          <details className="mt-2 pt-2 border-t border-zinc-100">
            <summary className="text-[11px] font-semibold text-zinc-700 cursor-pointer hover:text-emerald-700">
              💡 Kenapa sering STALE? — Setting Traccar Android
            </summary>
            <div className="mt-2 text-[10.5px] text-zinc-600 leading-snug space-y-1 pl-3">
              <div>Kalau device baru online sebentar lalu STALE, biasanya bukan bug backend. Backend selalu update timestamp setiap terima ping. Masalah umum di HP teknisi:</div>
              <div className="mt-1"><b>1. Battery Optimization aktif</b> → matikan utk aplikasi Traccar:</div>
              <div className="pl-3">Settings HP → Apps → Traccar Client → Battery → <b>Unrestricted</b> / <b>No restrictions</b></div>
              <div><b>2. Frequency & Distance terlalu longgar</b> → di dalam Traccar Client:</div>
              <div className="pl-3">Frequency: <b>60 detik</b> · Distance: <b>50 m</b> · Angle: <b>10°</b> · Accuracy: <b>High</b></div>
              <div><b>3. Doze/Standby mode</b> → tambahkan Traccar ke whitelist "Not Optimized" di Advanced battery settings.</div>
              <div><b>4. GPS provider off</b> → pastikan Location service HP ON dgn High Accuracy mode.</div>
              <div className="mt-2 pt-1 border-t border-zinc-100 text-emerald-800">Untuk cek: buka Traccar Client → lihat log — kalau ada banyak <code>"Location filtered"</code> tapi jarang <code>"Upload response 200"</code>, itu tanda filter client terlalu ketat.</div>
            </div>
          </details>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map(t => (
          <div key={t.id} className="border border-border bg-white rounded-sm p-4" data-testid={`tech-card-${t.id}`}>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full grid place-items-center text-white font-semibold" style={{background: t.color}}>
                {t.name.split(" ").map(x=>x[0]).slice(0,2).join("")}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold">{t.name}</div>
                <div className="text-[11px] text-zinc-500">{t.phone || "—"}</div>
                <div className="text-[11px] text-zinc-500">{t.base_address}</div>
              </div>
              <Button size="icon" variant="ghost" onClick={()=>del(t.id)} className="h-7 w-7 rounded-sm text-zinc-400 hover:text-red-500" data-testid={`delete-${t.id}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
            <div className="mt-3 flex flex-wrap gap-1">
              {t.skills.map(s => <Badge key={s} className="rounded-sm bg-zinc-100 text-zinc-700 text-[10px]">{s}</Badge>)}
            </div>
            <div className="mt-3 text-[10px] font-mono text-zinc-400">
              {t.base_lat?.toFixed?.(4)}, {t.base_lng?.toFixed?.(4)}
            </div>

            {/* v85.34 — Resume WO harian via WA (per teknisi) */}
            <div className="mt-3 pt-3 border-t border-border">
              <div className="flex items-center gap-1.5 mb-1.5">
                <Send className="w-3 h-3 text-emerald-700" />
                <span className="text-[10px] uppercase tracking-widest font-semibold text-zinc-500">
                  Resume WO Harian
                </span>
              </div>
              <div className="flex gap-1.5">
                <Button size="sm" variant="outline"
                  onClick={() => sendResume(t, { dryRun: true })}
                  disabled={resumeBusyId === t.id}
                  className="flex-1 rounded-sm h-7 text-[10px] gap-1"
                  data-testid={`tech-resume-preview-${t.id}`}>
                  {resumeBusyId === t.id
                    ? <Loader2 className="w-3 h-3 animate-spin" />
                    : <>Preview</>}
                </Button>
                <Button size="sm"
                  onClick={() => sendResume(t)}
                  disabled={resumeBusyId === t.id || !t.phone}
                  className="flex-1 rounded-sm h-7 text-[10px] gap-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid={`tech-resume-send-${t.id}`}
                  title={!t.phone ? "Nomor HP belum diisi" : "Kirim resume ke WA teknisi sekarang"}>
                  <Send className="w-3 h-3" /> Kirim Sekarang
                </Button>
              </div>
            </div>

            {/* Google Calendar link */}
            <div className="mt-3 pt-3 border-t border-border">
              <div className="flex items-center gap-1.5 mb-1.5">
                <CalIcon className="w-3 h-3 text-[#2962FF]" />
                <span className="text-[10px] uppercase tracking-widest font-semibold text-zinc-500">Google Calendar</span>
                {t.google_calendar_id && <CheckCircle2 className="w-3 h-3 text-emerald-600 ml-auto" />}
              </div>
              {!gStatus?.connected ? (
                <div className="text-[10px] text-zinc-400">Sambungkan admin di Pengaturan</div>
              ) : gCals.length === 0 ? (
                <div className="text-[10px] text-zinc-400">Memuat kalender…</div>
              ) : (
                <Select value={t.google_calendar_id || ""} onValueChange={(v)=>linkCal(t.id, v)}>
                  <SelectTrigger className="h-8 rounded-sm text-[11px]" data-testid={`link-cal-${t.id}`}>
                    <SelectValue placeholder="Pilih kalender…" />
                  </SelectTrigger>
                  <SelectContent className="max-h-64">
                    {gCals.filter(c => c.can_write).map(c => (
                      <SelectItem key={c.id} value={c.id}>
                        <span className="inline-flex items-center gap-1.5">
                          <span className="w-2 h-2 rounded-full" style={{background: c.backgroundColor}} />
                          {c.summary}{c.primary ? " (utama)" : ""}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Zone assignment */}
            <div className="mt-3 pt-3 border-t border-border">
              <div className="flex items-center gap-1.5 mb-1.5">
                <MapPin className="w-3 h-3 text-[#E65100]" />
                <span className="text-[10px] uppercase tracking-widest font-semibold text-zinc-500">Zona Kerja (KML)</span>
                {t.zone_key && <CheckCircle2 className="w-3 h-3 text-emerald-600 ml-auto" />}
              </div>
              {zones.length === 0 ? (
                <div className="text-[10px] text-zinc-400">Belum ada zona di-load. Klik <b>Refresh Zona</b> di atas.</div>
              ) : (
                <Select value={t.zone_key || "__none__"} onValueChange={(v)=>setTechZone(t, v==="__none__" ? null : v)}>
                  <SelectTrigger className="h-8 rounded-sm text-[11px]" data-testid={`zone-select-${t.id}`}>
                    <SelectValue placeholder="Pilih zona…" />
                  </SelectTrigger>
                  <SelectContent className="max-h-64">
                    <SelectItem value="__none__">— tidak ada —</SelectItem>
                    {zones.map(z => (
                      <SelectItem key={z.zone_key} value={z.zone_key}>
                        <span className="font-mono text-[11px]">{z.zone_key}</span>
                        <span className="text-zinc-500 ml-2">{z.name}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Password Armada management */}
            <div className="mt-3 pt-3 border-t border-border flex items-center justify-between gap-2">
              <div className="flex items-center gap-1.5 text-[10px] text-zinc-500">
                <KeyRound className="w-3 h-3" />
                <span>Login Armada:</span>
                {t.has_password ? (
                  <span className="font-mono text-emerald-700 font-semibold" data-testid={`tech-user-${t.id}`}>
                    @{t.username || "?"}
                  </span>
                ) : (
                  <span className="text-zinc-400 italic">belum diset</span>
                )}
              </div>
              <Button size="sm" variant="outline" onClick={() => openPwDialog(t)}
                className="rounded-sm h-7 text-[10px] gap-1" data-testid={`tech-set-password-btn-${t.id}`}>
                <KeyRound className="w-3 h-3" />
                {t.has_password ? "Reset" : "Set"} Password
              </Button>
            </div>
            {/* v84.6 — GPS Push Link */}
            {t.gps_token && (
              <div className="mt-2 pt-2 border-t border-border flex items-center justify-between gap-2">
                <div className="flex items-center gap-1.5 text-[10px] text-zinc-500 min-w-0">
                  <MapPin className="w-3 h-3 flex-none" />
                  <span className="flex-none">GPS Push:</span>
                  <span className="font-mono text-[10px] text-zinc-600 truncate">
                    /tech/gps?token={t.gps_token.slice(0, 8)}…
                  </span>
                </div>
                <Button size="sm" variant="outline"
                  onClick={() => {
                    const url = `${window.location.origin}/tech/gps?token=${t.gps_token}`;
                    navigator.clipboard.writeText(url).then(() => toast.success("Link GPS disalin"));
                  }}
                  className="rounded-sm h-7 text-[10px] gap-1"
                  data-testid={`tech-copy-gps-link-${t.id}`}>
                  <Copy className="w-3 h-3" />
                  Copy Link GPS
                </Button>
              </div>
            )}
            {/* v84.22 — Traccar Device ID */}
            <div className="mt-2 pt-2 border-t border-border">
              <div className="flex items-center gap-1.5 mb-1">
                <Navigation className="w-3 h-3 text-emerald-600" />
                <span className="text-[10px] uppercase tracking-widest font-semibold text-zinc-500">Traccar Client</span>
                {t.traccar_id && <CheckCircle2 className="w-3 h-3 text-emerald-600 ml-auto" />}
              </div>
              <div className="flex gap-1.5">
                <Input
                  defaultValue={t.traccar_id || ""}
                  placeholder="Device ID (mis. 99061355)"
                  className="rounded-sm h-8 text-[11px] font-mono flex-1"
                  data-testid={`traccar-id-${t.id}`}
                  onBlur={(e) => {
                    if ((e.target.value || "").trim() !== (t.traccar_id || "")) {
                      saveTraccarId(t, e.target.value);
                    }
                  }}
                />
                <Button
                  size="sm" variant="outline"
                  onClick={() => {
                    const url = `${window.location.origin.replace(/^https?:\/\//,'https://')}/api/public/traccar/${t.gps_token}`;
                    navigator.clipboard.writeText(url).then(() => toast.success("URL Traccar disalin"));
                  }}
                  className="rounded-sm h-8 text-[10px] gap-1 whitespace-nowrap"
                  data-testid={`traccar-copy-url-${t.id}`}>
                  <Copy className="w-3 h-3" />URL
                </Button>
              </div>
              <div className="text-[9px] text-zinc-400 mt-1 leading-snug">
                Setup Traccar Client → Server URL: <code className="font-mono">.../api/public/traccar/{t.gps_token?.slice(0,6)}…</code>
              </div>
            </div>
          </div>
        ))}
        {!items.length && (
          <div className="col-span-full border border-dashed border-border rounded-sm p-12 text-center text-zinc-500">
            Belum ada teknisi. Klik <b>Seed 9 Contoh</b> untuk mulai cepat.
          </div>
        )}
      </div>

      {/* v85.34 — Preview resume harian dialog */}
      <Dialog open={!!resumePreview} onOpenChange={(o) => !o && setResumePreview(null)}>
        <DialogContent className="rounded-sm max-w-xl" data-testid="resume-preview-dialog">
          <DialogHeader>
            <DialogTitle>Preview Resume — {resumePreview?.name}</DialogTitle>
          </DialogHeader>
          {resumePreview && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-[11px] text-zinc-500">
                <Badge className="rounded-sm bg-zinc-100 text-zinc-700">
                  {resumePreview.total} WO total
                </Badge>
                <Badge className="rounded-sm bg-amber-100 text-amber-800">
                  {resumePreview.special_count} butuh perhatian
                </Badge>
              </div>
              <pre className="whitespace-pre-wrap text-[12px] bg-zinc-50 border border-zinc-200 rounded-sm p-3 max-h-[400px] overflow-auto font-mono"
                data-testid="resume-preview-text">
                {resumePreview.text}
              </pre>
              <div className="text-[10px] text-zinc-400 border-t border-border pt-2">
                💡 Ini hanya preview — klik <b>Kirim Sekarang</b> di kartu teknisi untuk benar-benar mengirim via WhatsApp.
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Password Armada dialog */}
      <Dialog open={!!pwDialog} onOpenChange={(o)=>!o && setPwDialog(null)}>
        <DialogContent className="rounded-sm" data-testid="tech-password-dialog">
          <DialogHeader>
            <DialogTitle>Login Armada · {pwDialog?.tech?.name}</DialogTitle>
          </DialogHeader>
          {pwDialog && (
            <div className="space-y-3">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">ID Armada (username)</label>
                <Input value={pwDialog.username}
                  onChange={e => setPwDialog({...pwDialog, username: e.target.value})}
                  placeholder="mis. Armada GA-02"
                  className="rounded-sm mt-1" data-testid="pw-username-input" />
                <div className="text-[9px] text-zinc-400 mt-0.5">Akan dinormalisasi jadi <code className="font-mono">armada-ga-02</code>. Teknisi login pakai teks aslinya.</div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Password (min 6 karakter)</label>
                <Input type="text" value={pwDialog.password}
                  onChange={e => setPwDialog({...pwDialog, password: e.target.value})}
                  placeholder="Password baru"
                  className="rounded-sm mt-1 font-mono" data-testid="pw-password-input" />
              </div>
              <div className="flex gap-2">
                <Button onClick={savePw} className="flex-1 bg-[#E65100] hover:bg-[#F57C00] rounded-sm"
                  data-testid="pw-save-btn">Simpan</Button>
                {pwDialog.password && (
                  <Button variant="outline" onClick={copyCred}
                    className="rounded-sm gap-1.5" data-testid="pw-copy-btn">
                    <Copy className="w-3.5 h-3.5" />Salin
                  </Button>
                )}
              </div>
              <div className="text-[10px] text-zinc-400 border-t border-border pt-2">
                💡 Setelah simpan, share kredensial ke teknisi. Mereka login di{" "}
                <code className="font-mono">{window.location.origin}/tech-login</code>
              </div>
              <TechDevicesList techId={pwDialog.tech.id} />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
